import { optionalQueryParams } from "../apiEndpointHelpers";
import type { ApiEndpointBuilder } from "../apiEndpointHelpers";

import type {
  AdminV2GrowthTagBulkJobResponse,
  AdminV2GrowthTagBulkMode,
  AdminV2GrowthTagBulkPreviewResponse,
  AdminV2GrowthTagBulkSourceContext,
  AdminV2GrowthTagDeleteResponse,
  AdminV2GrowthTagMergePreviewResponse,
  AdminV2GrowthTagMergeResponse,
  AdminV2GrowthTagRecordResponse,
  AdminV2GrowthTagScopeFilter,
  AdminV2GrowthTagScopeType,
  AdminV2GrowthTagsListResponse,
} from "@/types/adminV2";

export const buildAdminV2GrowthTagsEndpoints = (
  builder: ApiEndpointBuilder,
) => ({
  getAdminV2GrowthTags: builder.query<
    AdminV2GrowthTagsListResponse,
    {
      organisationId?: string;
      scopeType?: AdminV2GrowthTagScopeFilter;
      q?: string;
      category?: string;
      status?: "active" | "archived";
      includeTagKeys?: string;
      limit?: number;
      offset?: number;
    } | void
  >({
    query: (params) => ({
      url: "/admin/v2/growth/tags",
      params: optionalQueryParams(params),
    }),
    providesTags: [{ type: "AdminV2GrowthTags", id: "LIST" }],
    keepUnusedDataFor: 20,
  }),

  // TAG-04: a tag is a name and a status only; the API refuses other fields
  // (tag_field_not_supported).
  createAdminV2GrowthTag: builder.mutation<
    AdminV2GrowthTagRecordResponse,
    {
      organisationId: string | null;
      scopeType?: AdminV2GrowthTagScopeType;
      name: string;
      slug?: string;
    }
  >({
    query: (body) => ({
      url: "/admin/v2/growth/tags",
      method: "POST",
      data: body,
    }),
    invalidatesTags: [
      { type: "AdminV2GrowthTags", id: "LIST" },
      { type: "AdminV2AuditLog", id: "LIST" },
    ],
  }),

  updateAdminV2GrowthTag: builder.mutation<
    AdminV2GrowthTagRecordResponse,
    {
      tagId: string;
      body: {
        name?: string;
        slug?: string;
      };
    }
  >({
    query: ({ tagId, body }) => ({
      url: `/admin/v2/growth/tags/${tagId}`,
      method: "PUT",
      data: body,
    }),
    invalidatesTags: (_result, _error, { tagId }) => [
      { type: "AdminV2GrowthTags", id: "LIST" },
      { type: "AdminV2GrowthTags", id: tagId },
      { type: "AdminV2AuditLog", id: "LIST" },
    ],
  }),

  deleteAdminV2GrowthTag: builder.mutation<
    AdminV2GrowthTagDeleteResponse,
    { tagId: string }
  >({
    query: ({ tagId }) => ({
      url: `/admin/v2/growth/tags/${tagId}`,
      method: "DELETE",
    }),
    invalidatesTags: (_result, _error, { tagId }) => [
      { type: "AdminV2GrowthTags", id: "LIST" },
      { type: "AdminV2GrowthTags", id: tagId },
      { type: "AdminV2AuditLog", id: "LIST" },
    ],
  }),

  mergeAdminV2GrowthTags: builder.mutation<
    AdminV2GrowthTagMergeResponse,
    { sourceTagId: string; targetTagId: string }
  >({
    query: (body) => ({
      url: "/admin/v2/growth/tags/merge",
      method: "POST",
      data: body,
    }),
    invalidatesTags: [
      { type: "AdminV2GrowthTags", id: "LIST" },
      { type: "AdminV2AuditLog", id: "LIST" },
    ],
  }),

  previewAdminV2GrowthTagMerge: builder.mutation<
    AdminV2GrowthTagMergePreviewResponse,
    { sourceTagId: string; targetTagId: string }
  >({
    query: (body) => ({
      url: "/admin/v2/growth/tags/merge-preview",
      method: "POST",
      data: body,
    }),
  }),

  previewAdminV2GrowthTagBulk: builder.mutation<
    AdminV2GrowthTagBulkPreviewResponse,
    {
      organisationId: string;
      tagId: string;
      operation: AdminV2GrowthTagBulkMode;
      personIds: string[];
      sourceContext?: AdminV2GrowthTagBulkSourceContext;
    }
  >({
    query: (body) => ({
      url: "/admin/v2/growth/tags/bulk-preview",
      method: "POST",
      data: body,
    }),
  }),

  bulkApplyAdminV2GrowthTag: builder.mutation<
    AdminV2GrowthTagBulkJobResponse,
    {
      organisationId: string;
      tagId: string;
      personIds: string[];
      sourceContext?: AdminV2GrowthTagBulkSourceContext;
    }
  >({
    query: (body) => ({
      url: "/admin/v2/growth/tags/bulk-apply",
      method: "POST",
      data: body,
    }),
    invalidatesTags: [
      { type: "AdminV2Jobs", id: "LIST" },
      { type: "AdminV2AuditLog", id: "LIST" },
    ],
  }),

  bulkRemoveAdminV2GrowthTag: builder.mutation<
    AdminV2GrowthTagBulkJobResponse,
    {
      organisationId: string;
      tagId: string;
      personIds: string[];
      sourceContext?: AdminV2GrowthTagBulkSourceContext;
    }
  >({
    query: (body) => ({
      url: "/admin/v2/growth/tags/bulk-remove",
      method: "POST",
      data: body,
    }),
    invalidatesTags: [
      { type: "AdminV2Jobs", id: "LIST" },
      { type: "AdminV2AuditLog", id: "LIST" },
    ],
  }),
});
